// =============================================================================
// SIREPPAR - Secciones de alianza en la tab "Datos del Trámite"
// =============================================================================
// Identificación, datos operativos y el listado de partidos integrantes. Este
// último es SOLO INFORMATIVO: las acciones de estado (verificar un integrante que
// no está en SIREPPAR) viven en la tab Documentación, junto al resto de lo que hay
// que revisar. Repetir el botón acá haría creer que son dos cosas distintas.
// =============================================================================

import { Link } from 'react-router-dom';
import { formatFechaCalendario } from '../../../utils/fechas';
import type { IntegranteAlianza } from './PanelIntegrantesAlianza';

export interface DatosAlianza {
  id: string;
  numero: number;
  denominacion: string;
  sigla?: string | null;
  tipo?: string | null;
  estado?: string | null;
  domicilioLegal?: string | null;
  localidad?: string | null;
  codigoPostal?: string | null;
  emailOficial?: string | null;
  telefonoOficial?: string | null;
  fechaReconocimiento?: string | null;
  resolucionReconocimiento?: string | null;
  convocatoria?: {
    id: string;
    denominacion: string;
    codigoCorto: string;
    fechaActo: string;
    ambitoTipo?: string | null;
  } | null;
}

export interface ApoderadoAlianza {
  id: string;
  dni: string;
  nombre: string;
  apellido: string;
  email?: string | null;
  telefono?: string | null;
}

interface Props {
  alianza: DatosAlianza;
  integrantes: IntegranteAlianza[];
  apoderados: ApoderadoAlianza[];
}

const TIPO_LABEL: Record<string, string> = {
  MUNICIPAL: 'Municipal',
  DISTRITO: 'Distrito',
  PROVINCIAL: 'Provincial',
  NACIONAL: 'Nacional',
};

function Fila({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-gray-100">
      <span className="text-gray-500 flex-shrink-0">{etiqueta}</span>
      <span className="text-right break-words">{children}</span>
    </div>
  );
}

export default function DatosAlianzaTramite({ alianza, integrantes, apoderados }: Props) {
  return (
    <>
      <div>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4">
          Identificación de la alianza
        </h4>
        <div className="space-y-3 text-sm">
          <Fila etiqueta="Denominación">
            <span className="font-medium">{alianza.denominacion}</span>
          </Fila>
          <Fila etiqueta="Sigla">{alianza.sigla || '—'}</Fila>
          <Fila etiqueta="Número">{alianza.numero}</Fila>
          <Fila etiqueta="Ámbito territorial">
            {alianza.tipo ? TIPO_LABEL[alianza.tipo] || alianza.tipo : '—'}
          </Fila>
          <Fila etiqueta="Convocatoria">{alianza.convocatoria?.denominacion || '—'}</Fila>
          <Fila etiqueta="Acto electoral">{formatFechaCalendario(alianza.convocatoria?.fechaActo)}</Fila>
          {alianza.resolucionReconocimiento && (
            <Fila etiqueta="Resolución de reconocimiento">
              <span className="font-medium">{alianza.resolucionReconocimiento}</span>
              {alianza.fechaReconocimiento && (
                <span className="text-gray-500"> · {formatFechaCalendario(alianza.fechaReconocimiento)}</span>
              )}
            </Fila>
          )}
        </div>
      </div>

      <div>
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4">
          Datos operativos
        </h4>
        <div className="space-y-3 text-sm">
          <Fila etiqueta="Domicilio legal">{alianza.domicilioLegal || '—'}</Fila>
          <Fila etiqueta="Localidad">{alianza.localidad || '—'}</Fila>
          <Fila etiqueta="Código postal">{alianza.codigoPostal || '—'}</Fila>
          <Fila etiqueta="Correo oficial">{alianza.emailOficial || '—'}</Fila>
          <Fila etiqueta="Teléfono">{alianza.telefonoOficial || '—'}</Fila>
        </div>

        {apoderados.length > 0 && (
          <>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4 mt-8">
              Apoderados
            </h4>
            <div className="space-y-3 text-sm">
              {apoderados.map((a) => (
                <Fila key={a.id} etiqueta={`${a.apellido}, ${a.nombre}`}>
                  <span className="text-gray-600">DNI {a.dni}</span>
                  {a.email && <span className="text-gray-500"> · {a.email}</span>}
                </Fila>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Listado informativo. Las acciones están en la tab Documentación. */}
      <div className="md:col-span-2">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2 mb-4">
          Partidos integrantes ({integrantes.length})
        </h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Agrupación</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ámbito</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Localidad</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Origen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {integrantes.map((i) => (
                <tr key={i.id}>
                  <td className="px-3 py-2">
                    {i.enSireppar && i.partidoId ? (
                      <Link to={`/partidos/${i.partidoId}`} className="text-blue-600 hover:underline font-medium">
                        {i.denominacion}
                      </Link>
                    ) : (
                      <span className="font-medium text-gray-900">{i.denominacion}</span>
                    )}
                    {i.sigla && <span className="text-gray-500"> ({i.sigla})</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {i.tipo ? TIPO_LABEL[i.tipo] || i.tipo : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{i.localidad || '—'}</td>
                  <td className="px-3 py-2">
                    {i.enSireppar ? (
                      <span className="text-blue-700">Registrado en SIREPPAR</span>
                    ) : i.verificadoEn ? (
                      <span className="text-green-700">Declarado — verificado</span>
                    ) : (
                      <span className="text-amber-700 font-medium">Declarado — sin verificar</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
